import { Autocomplete, Box, Button, CircularProgress, Icon, MenuItem, Paper, Select, TextField, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { FazendaService } from '../../services/api/cadastros/FazendaService';
import { useDebounce } from '../../hooks';
import { Environment } from '../../environment';

interface IDadosSelect {
  nameBD: string;
  descricao: string;
}

type TAutoCompleteOption = {
  idfazenda: string;
  label: string;
}

interface IFerramentasDaListagemProps {
  dadosSelecao?: IDadosSelect[];
  dataInicial?: string;
  dataFinal?: string;
  mostrarInputData?: boolean;
  mostrarInputFazenda?: boolean;
  mostrarInputBusca?: boolean;
  aoMudarDataInicial?: (novoTexto: string) => void;
  aoMudarDataFinal?: (novoTexto: string) => void;
  aoMudarFazenda?: (novoTexto: string) => void;
  aoMudarParceiro?: (novoTexto: string) => void;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  aoClicarEmNovo?: () => void;
  mostrarInputBasicSelect?: boolean
  textoDoSelect?: string;
  textoDaBusca?: string;
  aoMudarTextoDoSelect?: (novoTextoselect: string) => void;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
}

export const FerramentasDaListagemOS: React.FC<IFerramentasDaListagemProps> = ({
  dataInicial = '',
  textoDaBusca = '',
  aoMudarDataInicial,
  dataFinal = '',
  aoMudarDataFinal,
  aoMudarFazenda,
  mostrarInputData = false,
  mostrarInputFazenda = false,
  mostrarInputBusca = false,
  aoClicarEmNovo,
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
  dadosSelecao =[],  
  mostrarInputBasicSelect = false,
  textoDoSelect = '',
  aoMudarTextoDoSelect,
  aoMudarTextoDeBusca,
}) => {
  const theme = useTheme();
  const partesDaDataInicial = dataInicial.split('/');
  const dataInicialFormatada = partesDaDataInicial[2] + '-' + partesDaDataInicial[1] + '-' + partesDaDataInicial[0];

  const partesDaDataFinal = dataFinal.split('/');
  const dataFinalFormatada = partesDaDataFinal[2] + '-' + partesDaDataFinal[1] + '-' + partesDaDataFinal[0];


  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>();

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      FazendaService.getAlllist( busca)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            // alert(result.message);
          } else {
            setOpcoes(result.data.map(fazenda => ({ idfazenda: fazenda.idfazenda, label: fazenda.descricao })));
          }
        });
    });
  }, [busca, selectedId]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.idfazenda === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <Box
      gap={1}
      marginX={3}
      paddingX={2}
      display="flex"
      alignItems="center"
      height={theme.spacing(5)}
      component={Paper}
      sx={{ height: '60px' }}
    >
      {mostrarInputBasicSelect && (
        <Select
          size="small"
          sx={{ minWidth: 170 }}
          value={textoDoSelect}
          onChange={(event) => aoMudarTextoDoSelect?.(event.target.value)}
        >
          {dadosSelecao.map((item) => (
            <MenuItem key={item.nameBD} value={item.nameBD}>
              {item.descricao}
            </MenuItem>
          ))}
        </Select>
      )}

      {mostrarInputBusca && (
        <TextField
          size="small"
          value={textoDaBusca}
          placeholder={Environment.INPUT_DE_BUSCA}
          onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
        />
      )} 

      {mostrarInputFazenda && (
        <Autocomplete
          sx={{ minWidth: 250}}
          openText='Abrir'
          closeText='Fechar'
          noOptionsText='Sem opções'
          loadingText='Carregando...'
  
          disablePortal
  
          options={opcoes}
          loading={isLoading}
          value={autoCompleteSelectedOption}
          onInputChange={(_, newValue) => setBusca(newValue)}
          onChange={(_, newValue) => { setSelectedId(newValue?.idfazenda); aoMudarFazenda?.(newValue?.idfazenda || ''); setBusca('');}}
          popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Fazenda"
            />
          )}
        />
      )}

      {mostrarInputData && (

        <LocalizationProvider  dateAdapter={AdapterDayjs}>
        
          <DatePicker 
            sx={{ minWidth: 150, maxWidth: 150 }}
            label="Data Inicial" 
            format='DD/MM/YYYY'
            defaultValue={dayjs()}
            value={dayjs(dataInicialFormatada)}
            onChange={(date: Dayjs | null) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataInicial?.(novoTexto);
            }}
            slotProps={{
              textField: {    
                size:'small',
              }
            }}
          />

          <DatePicker 
            sx={{ minWidth: 150, maxWidth: 150}}
            label="Data Final" 
            format='DD/MM/YYYY'
            defaultValue={dayjs()}
            value={dayjs(dataFinalFormatada)}
            onChange={(date: Dayjs | null) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataFinal?.(novoTexto);
            }}
            slotProps={{
              textField: {    
                size:'small',
              }
            }}
          />
      
        </LocalizationProvider>
      )} 
      
      <Box flex={1} display="flex" justifyContent="end">
        {mostrarBotaoNovo && (
          <Button
            color='primary'
            disableElevation
            variant='contained'
            onClick={aoClicarEmNovo}
            endIcon={<Icon>add</Icon>}
          >{textoBotaoNovo}</Button>
        )}
      </Box>
    </Box>
  );
};